/** Firebase accounts (DEPLOYMENT.md M2) — the fallback sign-in provider for
 * studios that haven't moved to Supabase (R4). See `accounts.ts` for the
 * shared `Accounts` contract and the dispatcher that picks between providers;
 * this file only knows how to talk to Firebase Auth.
 */

import { api } from "@/lib/api";
import { setTokenProvider, writeIdCookie } from "@/lib/auth-token";
import type { AccountUser, Accounts } from "@/lib/accounts";

interface FirebaseConfigOut {
  enabled: boolean;
  api_key: string;
  auth_domain: string;
  project_id: string;
  app_id: string;
}

/** Resolve Firebase-backed accounts, or `null` if unconfigured (older
 * backend, or the operator hasn't set the FIREBASE_* env vars). */
export async function initFirebaseAccounts(): Promise<Accounts | null> {
  let config: FirebaseConfigOut;
  try {
    config = await api.request<FirebaseConfigOut>("GET", "/api/auth/firebase/config");
  } catch {
    return null; // older backend
  }
  if (!config.enabled) return null;

  const { initializeApp } = await import("firebase/app");
  const {
    GoogleAuthProvider,
    getAuth,
    onIdTokenChanged,
    signInWithPopup,
    signOut,
  } = await import("firebase/auth");

  const app = initializeApp({
    apiKey: config.api_key,
    authDomain: config.auth_domain,
    projectId: config.project_id,
    appId: config.app_id,
  });
  const auth = getAuth(app);

  // Let the token layer pull a freshly-minted token on demand (proactive timer,
  // visibility/online wake-ups, and 401 recovery all route through here).
  setTokenProvider(() => auth.currentUser?.getIdToken(true) ?? Promise.resolve(null));

  // Keep the cookie in lock-step with the SDK's token refreshes. `writeIdCookie`
  // derives the cookie's max-age from the JWT's real exp and re-arms the
  // proactive refresh timer, so the cookie never outlives its token.
  onIdTokenChanged(auth, (user) => {
    if (user) void user.getIdToken().then(writeIdCookie);
    else writeIdCookie(null);
  });

  const toUser = (u: {
    uid: string;
    email: string | null;
    displayName: string | null;
    photoURL: string | null;
  }): AccountUser => ({
    uid: u.uid,
    email: u.email,
    displayName: u.displayName,
    photoURL: u.photoURL,
  });

  return {
    provider: "firebase",
    signInWithGoogle: async () => {
      const cred = await signInWithPopup(auth, new GoogleAuthProvider());
      writeIdCookie(await cred.user.getIdToken());
      return toUser(cred.user);
    },
    signOut: async () => {
      await signOut(auth);
      writeIdCookie(null);
    },
    onUser: (cb) => onIdTokenChanged(auth, (u) => cb(u ? toUser(u) : null)),
  };
}
